/**
 * extraction_rules 변환 헬퍼 (M13 / C-1).
 *
 * ExtractionRulesEditor 컴포넌트가 사용하는 순수 함수들.
 * react-refresh 가 컴포넌트 파일에서만 export 를 허용하므로 분리.
 */

import { LLM_FIELD_KEYS } from "@/features/records/fieldLabels";

/**
 * invoice_extractor.py 의 `Fields:` 블록에서 추출한 표준 필드.
 * 새 필드가 추가되면 ``features/records/fieldLabels.ts`` 의 ``LLM_FIELDS`` 에 추가.
 *
 * 본 export 는 backward-compat 용 alias — 컴포넌트는 가능하면
 * ``LLM_FIELDS`` 를 직접 import 하여 i18n 라벨까지 함께 사용할 것.
 */
export const KNOWN_INVOICE_FIELDS = LLM_FIELD_KEYS;

/** 단일 필드 → hint 매핑. 폼 모드의 row 1줄에 대응. */
export interface RulesFormEntry {
  field: string;
  hint: string;
}

/**
 * rulesText (자유 JSON 문자열) → 폼 모드에서 다룰 entries 리스트.
 *
 * 변환 안전 케이스:
 *   - 빈 문자열 / null → []  (formCompatible=true)
 *   - 객체이고 모든 값이 `{ hint: string }` 단일 키 → entries 로 분해
 *
 * 변환 불가능 (formCompatible=false):
 *   - JSON parse 실패 → jsonError 채워짐
 *   - 객체가 아님 (배열/원시값)
 *   - 값에 hint 외 다른 키가 섞여있거나 hint 가 문자열이 아닌 경우
 *
 * formCompatible=false 일 때 UI 는 폼 모드 토글을 비활성화하여 데이터 손실을 막는다.
 */
export interface ParsedRules {
  entries: RulesFormEntry[];
  formCompatible: boolean;
  jsonError: string | null;
}

export function parseRulesForForm(rulesText: string): ParsedRules {
  const trimmed = rulesText.trim();
  if (!trimmed) {
    return { entries: [], formCompatible: true, jsonError: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return {
      entries: [],
      formCompatible: false,
      jsonError: err instanceof Error ? err.message : "invalid JSON",
    };
  }

  if (parsed === null) {
    return { entries: [], formCompatible: true, jsonError: null };
  }
  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    return { entries: [], formCompatible: false, jsonError: "expected object" };
  }

  const entries: RulesFormEntry[] = [];
  let compatible = true;
  for (const [field, raw] of Object.entries(parsed as Record<string, unknown>)) {
    if (
      raw === null ||
      typeof raw !== "object" ||
      Array.isArray(raw) ||
      Object.keys(raw).length !== 1 ||
      !("hint" in raw) ||
      typeof (raw as { hint: unknown }).hint !== "string"
    ) {
      compatible = false;
      break;
    }
    entries.push({ field, hint: (raw as { hint: string }).hint });
  }

  if (!compatible) {
    return { entries: [], formCompatible: false, jsonError: null };
  }
  return { entries, formCompatible: true, jsonError: null };
}

/** entries → JSON 문자열 (2-space indent). 빈 row 는 제외, 모두 빈 경우 빈 문자열. */
export function serializeEntriesToJson(entries: RulesFormEntry[]): string {
  const valid = entries.filter((e) => e.field.trim() && e.hint.trim());
  if (valid.length === 0) return "";
  const obj: Record<string, { hint: string }> = {};
  for (const { field, hint } of valid) {
    obj[field.trim()] = { hint: hint.trim() };
  }
  return JSON.stringify(obj, null, 2);
}
